import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import React, { useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FormEditor from "@/components/features/forms/form-editor";
import { CreateFormMutationData, FormEditorRef, SaveFormResponse } from "@/components/features/forms/form-editor/types";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { toast } from "sonner";
import { TRPCClientError } from "@trpc/client";
import { formToSaveFormResponse, saveFormResponseToUpdateForm } from "@/components/features/forms/form-editor/helpers";
import { TForm, TReportType } from "@/types/trpc.types";
import * as Icons from "lucide-react"
import MultiSelect from "@/components/ui/multi-select";

interface Props {
    report_template_id: string;
    trigger?: React.ReactNode
    onUpdated?: () => void
}

const formSchema = z.object({
    name: z.string().min(1, "Name is required"),
    type: z.string().min(1, "Type is required").array(),
})

const EditReportTemplate = ({ report_template_id, trigger, onUpdated }: Props) => {
    const { trpc } = useTRPC()
    const qc = useQueryClient();
    const [open, setOpen] = React.useState(false);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
    });

    const formRef = useRef<FormEditorRef>(null);

    // Fetch the report ONLY when the sheet is open
    const reportQuery = useQuery({
        queryKey: ["reports", "byId", report_template_id],
        queryFn: async () => trpc.reports.getById.query({ report_id: report_template_id }),
        enabled: open && !!report_template_id,
        // ensures fresh data every time you open the sheet
        refetchOnMount: "always",
    });

    // Fetch the form ONLY when the sheet is open and form_id is known
    const formQuery = useQuery({
        queryKey: ["forms", "byId", reportQuery.data?.form_id],
        queryFn: async () => {
            const id = reportQuery.data?.form_id;
            if (!id) return;
            return trpc.forms.getById.query({ form_id: id });
        },
        enabled: open && !!reportQuery.data?.form_id,
        refetchOnMount: "always",
    });

    useEffect(() => {
        if (!reportQuery.data) return;
        form.setValue("name", reportQuery.data.name ?? "");
        form.setValue("type", reportQuery.data.type);
    }, [reportQuery.data]);

    useEffect(() => {
        if (!formQuery.data) return;
        formRef.current?.loadForm(formToSaveFormResponse(formQuery.data as TForm));
    }, [formQuery.data]);

    interface UpdateReportMutationData {
        report_id: string;
        name?: string;
        type?: TReportType[];
        form?: CreateFormMutationData;
    }

    const updateReportMutation = useMutation({
        mutationFn: async (data: UpdateReportMutationData) => {
            await trpc.reports.templates.update.mutate(data)
        },
        onSuccess: async (_, vars) => {
            toast.success("Sjabloon succesvol gewijzigd");

            // Optional optimistic cache nudge so UI reflects changes immediately
            qc.setQueryData(["reports", "byId", report_template_id], (old: any) =>
                old ? { ...old, name: form.getValues().name, type: form.getValues().type } : old
            );

            // If your save pipeline returns a new/updated form, you could also update that cache:
            // qc.setQueryData(["forms","byId", reportQuery.data?.form_id], newFormData);

            // Make sure ALL related caches refetch next time:
            await Promise.all([
                qc.invalidateQueries({ queryKey: ["report-templates"], exact: false }),     // lists
                qc.invalidateQueries({ queryKey: ["reports", "byId", report_template_id] }), // this report
                qc.invalidateQueries({ queryKey: ["forms", "byId", reportQuery.data?.form_id] }), // the form
            ]);

            setOpen(false);
            onUpdated?.(); // optional now, but fine to keep
        },
        onError: (err) => {
            if (err instanceof TRPCClientError) {
                toast.error(err.message);
            } else if (err instanceof Error) {
                toast.error(err.message);
            } else {
                toast.error("Er is een onbekende fout opgetreden");
            }
        }
    });

    const handleSubmit = () => {
        if (!form.formState.isValid || !formRef.current) return;

        const values = form.getValues();
        const formValues = formRef.current.saveForm();
        const formattedForm = saveFormResponseToUpdateForm(formValues);

        updateReportMutation.mutate({
            report_id: report_template_id,
            name: values.name,
            type: values.type as TReportType[],
            form: formattedForm,
        });
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                {trigger}
            </SheetTrigger>
            <SheetContent className="md:max-w-7xl">
                <SheetHeader>
                    <SheetTitle>Sjabloon wijzigen</SheetTitle>
                    <SheetDescription>Wijzig de velden om een sjabloon te wijzigen</SheetDescription>
                </SheetHeader>
                <div className="h-full overflow-auto px-4">
                    <div className="flex flex-col justify-between h-full">
                        <div className="flex flex-col gap-4 h-full">
                            <div className="space-y-1">
                                <Label>Naam</Label>
                                <Input {...form.register("name")} />
                            </div>
                            <div className="space-y-1">
                                <Label>Type rapport</Label>
                                <Controller
                                    control={form.control}
                                    name="type"
                                    render={({ field }) => (
                                        <MultiSelect
                                            selectedOptions={field.value}
                                            onValueChange={(value) => field.onChange(value)}
                                            options={[
                                                { value: "screening", label: "Screening" },
                                                { value: "scouting", label: "Scouting" },
                                                { value: "data", label: "Data" },
                                                { value: "check", label: "Check" },
                                                { value: "other", label: "Andere" },
                                            ]}
                                        />
                                    )}
                                />
                            </div>

                            <div className="overflow-auto h-full ">
                                <FormEditor
                                    ref={formRef}
                                    onReady={() => {
                                        if (!formQuery.data) return;
                                        formRef.current?.loadForm(formToSaveFormResponse(formQuery.data as TForm))
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                </div>
                <SheetFooter>
                    <div className="flex items-center justify-end gap-2">
                        <Button onClick={() => { setOpen(false) }} variant='outline'>Sluiten</Button>
                        <Button onClick={() => { handleSubmit() }} disabled={!form.formState.isValid || updateReportMutation.isLoading}>
                            {updateReportMutation.isLoading && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
                            Opslaan
                        </Button>
                    </div>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    )
}

export default EditReportTemplate